/**
 * Aurevra Theme — Main JavaScript
 * Handles: Navigation, Announcements, Fade-in animations, Cart drawer,
 * Product gallery, Variant selectors, Quantity controls, Accordion, Newsletter
 */

(function() {
  'use strict';

  /* =============================================
     NAVIGATION — Sticky + Scroll Effect
     ============================================= */
  const nav = document.getElementById('mainNav');
  if (nav) {
    let lastScroll = 0;
    window.addEventListener('scroll', function() {
      const currentScroll = window.scrollY;
      if (currentScroll > 60) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* =============================================
     MOBILE NAVIGATION TOGGLE
     ============================================= */
  const mobileToggle = document.getElementById('mobileToggle');
  const navLinks = document.getElementById('navLinks');
  if (mobileToggle && navLinks) {
    mobileToggle.addEventListener('click', function() {
      navLinks.classList.toggle('open');
      mobileToggle.classList.toggle('open');
    });
    navLinks.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', function() {
        navLinks.classList.remove('open');
        mobileToggle.classList.remove('open');
      });
    });
  }

  /* =============================================
     MOBILE DROPDOWN TOGGLE
     ============================================= */
  document.addEventListener('click', function(e) {
    var dropdownToggle = e.target.closest('.nav-dropdown__toggle');
    if (!dropdownToggle) return;
    e.preventDefault();
    var dropdown = dropdownToggle.closest('.nav-dropdown');
    if (!dropdown) return;
    // On mobile, toggle open class
    if (window.innerWidth <= 768) {
      dropdown.classList.toggle('open');
      // Close other dropdowns
      document.querySelectorAll('.nav-dropdown').forEach(function(d) {
        if (d !== dropdown) d.classList.remove('open');
      });
    }
  });

  /* =============================================
     ANNOUNCEMENT BAR SLIDER
     ============================================= */
  var announcementSlides = document.querySelectorAll('.announcement-bar__slide');
  var currentSlide = 0;
  var announcementTimer = null;

  function showAnnouncementSlide(index) {
    if (announcementSlides.length === 0) return;
    // Wrap index
    if (index < 0) index = announcementSlides.length - 1;
    if (index >= announcementSlides.length) index = 0;
    // Fade out current
    announcementSlides.forEach(function(slide, i) {
      slide.style.opacity = i === index ? '1' : '0';
    });
    currentSlide = index;
  }

  function slideAnnouncement(direction) {
    showAnnouncementSlide(currentSlide + direction);
    // Reset auto-advance timer
    if (announcementTimer) clearInterval(announcementTimer);
    if (announcementSlides.length > 1) {
      announcementTimer = setInterval(function() {
        showAnnouncementSlide(currentSlide + 1);
      }, 5000);
    }
  }

  // Auto-advance announcements
  if (announcementSlides.length > 1) {
    showAnnouncementSlide(0);
    announcementTimer = setInterval(function() {
      showAnnouncementSlide(currentSlide + 1);
    }, 5000);
  }

  /* =============================================
     INTERSECTION OBSERVER — Fade-in Animations
     ============================================= */
  const fadeElements = document.querySelectorAll('.fade-in');
  if (fadeElements.length > 0 && 'IntersectionObserver' in window) {
    const fadeObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          fadeObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });
    fadeElements.forEach(function(el) {
      fadeObserver.observe(el);
    });
  } else {
    fadeElements.forEach(function(el) {
      el.classList.add('visible');
    });
  }

  /* =============================================
     CART DRAWER
     ============================================= */
  const cartDrawerOverlay = document.getElementById('cartDrawerOverlay');
  const cartDrawer = document.getElementById('cartDrawer');
  const cartDrawerItems = document.getElementById('cartDrawerItems');
  const cartDrawerFooter = document.getElementById('cartDrawerFooter');
  const cartSubtotalEl = document.getElementById('cartSubtotal');
  const cartFreeShippingText = document.getElementById('cartFreeShippingText');
  const cartProgressFill = document.getElementById('cartProgressFill');

  function openCartDrawer() {
    if (cartDrawerOverlay) cartDrawerOverlay.classList.add('open');
    if (cartDrawer) cartDrawer.classList.add('open');
    document.body.style.overflow = 'hidden';
    loadCart();
  }

  function closeCartDrawer() {
    if (cartDrawerOverlay) cartDrawerOverlay.classList.remove('open');
    if (cartDrawer) cartDrawer.classList.remove('open');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-cart-toggle]').forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      openCartDrawer();
    });
  });

  if (cartDrawerOverlay) {
    cartDrawerOverlay.addEventListener('click', closeCartDrawer);
  }
  document.querySelectorAll('[data-cart-close]').forEach(function(btn) {
    btn.addEventListener('click', closeCartDrawer);
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') closeCartDrawer();
  });

  /* ---- Load Cart via AJAX ---- */
  var FREE_SHIPPING_THRESHOLD = 9900; // $99.00 in cents

  function loadCart() {
    fetch('/cart.js')
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        renderCart(cart);
        updateCartBadge(cart.item_count);
      })
      .catch(function(err) {
        console.error('Failed to load cart:', err);
      });
  }

  function renderCart(cart) {
    if (!cartDrawerItems) return;

    if (cart.items.length === 0) {
      cartDrawerItems.innerHTML =
        '<div class="cart-empty">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 01-8 0"/>' +
          '</svg>' +
          '<p>Your cart is empty</p>' +
        '</div>';
      if (cartDrawerFooter) cartDrawerFooter.style.display = 'none';
      if (document.querySelector('.cart-free-shipping')) {
        document.querySelector('.cart-free-shipping').style.display = 'none';
      }
      return;
    }

    if (cartDrawerFooter) cartDrawerFooter.style.display = '';
    if (document.querySelector('.cart-free-shipping')) {
      document.querySelector('.cart-free-shipping').style.display = '';
    }

    var html = '';
    cart.items.forEach(function(item) {
      var image = item.image || '';
      html +=
        '<div class="cart-item" data-variant-id="' + item.variant_id + '">' +
          '<div class="cart-item-image">' +
            (image ? '<img src="' + image + '" alt="' + (item.title || '').replace(/"/g, '&quot;') + '">' : '') +
          '</div>' +
          '<div class="cart-item-details">' +
            '<div class="cart-item-title">' + item.product_title + '</div>' +
            (item.variant_title ? '<div class="cart-item-variant">' + item.variant_title + '</div>' : '') +
            '<div class="cart-item-bottom">' +
              '<div class="cart-item-qty">' +
                '<button onclick="Aurevra.updateCartItem(' + item.variant_id + ', ' + (item.quantity - 1) + ')">&minus;</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button onclick="Aurevra.updateCartItem(' + item.variant_id + ', ' + (item.quantity + 1) + ')">+</button>' +
              '</div>' +
              '<div class="cart-item-price">' + formatMoney(item.line_price) + '</div>' +
            '</div>' +
            '<div class="cart-item-remove" onclick="Aurevra.updateCartItem(' + item.variant_id + ', 0)">Remove</div>' +
          '</div>' +
        '</div>';
    });
    cartDrawerItems.innerHTML = html;

    if (cartSubtotalEl) {
      cartSubtotalEl.textContent = formatMoney(cart.total_price);
    }

    var remaining = FREE_SHIPPING_THRESHOLD - cart.total_price;
    if (cartFreeShippingText) {
      if (remaining <= 0) {
        cartFreeShippingText.innerHTML = 'You qualify for <strong>free shipping</strong>!';
      } else {
        cartFreeShippingText.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more for free shipping';
      }
    }
    if (cartProgressFill) {
      var pct = Math.min((cart.total_price / FREE_SHIPPING_THRESHOLD) * 100, 100);
      cartProgressFill.style.width = pct + '%';
    }
  }

  function updateCartBadge(count) {
    document.querySelectorAll('.cart-badge').forEach(function(badge) {
      badge.textContent = count;
      badge.setAttribute('data-count', count);
      badge.style.display = count > 0 ? '' : 'none';
    });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* =============================================
     ADD TO CART (AJAX)
     ============================================= */
  function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    var btn = document.querySelector('.product-add-btn');
    var originalText = btn ? btn.textContent : '';

    if (btn) {
      btn.textContent = 'Adding...';
      btn.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(function() {
      if (btn) {
        btn.textContent = 'Added!';
        btn.style.background = '#5B8C6F';
      }
      loadCart();
      openCartDrawer();
      setTimeout(function() {
        if (btn) {
          btn.textContent = originalText;
          btn.style.background = '';
          btn.disabled = false;
        }
      }, 1500);
    })
    .catch(function(err) {
      console.error('Add to cart error:', err);
      if (btn) {
        btn.textContent = 'Error — Try Again';
        btn.disabled = false;
        setTimeout(function() {
          btn.textContent = originalText;
        }, 2000);
      }
    });
  }

  function updateCartItem(variantId, quantity) {
    if (quantity <= 0) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      })
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        renderCart(cart);
        updateCartBadge(cart.item_count);
      });
    } else {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      })
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        renderCart(cart);
        updateCartBadge(cart.item_count);
      });
    }
  }

  /* =============================================
     QUICK ADD (from product cards)
     ============================================= */
  document.addEventListener('click', function(e) {
    var quickAddBtn = e.target.closest('.product-card__quick-add, .product-quick-add');
    if (quickAddBtn) {
      e.preventDefault();
      e.stopPropagation();
      var variantId = quickAddBtn.getAttribute('data-product-id') || quickAddBtn.getAttribute('data-variant-id');
      if (variantId) {
        addToCart(variantId, 1);
      }
    }
  });

  /* =============================================
     PRODUCT GALLERY — Thumbnail Switcher
     ============================================= */
  document.addEventListener('click', function(e) {
    var thumb = e.target.closest('.product-thumbnail');
    if (!thumb) return;

    var gallery = thumb.closest('.product-gallery');
    if (!gallery) return;

    gallery.querySelectorAll('.product-thumbnail').forEach(function(t) {
      t.classList.remove('active');
    });
    thumb.classList.add('active');

    var thumbImg = thumb.querySelector('img');
    var mainImg = gallery.querySelector('.product-main-image img');
    if (thumbImg && mainImg) {
      mainImg.style.opacity = '0';
      setTimeout(function() {
        mainImg.src = thumbImg.src;
        mainImg.style.opacity = '1';
      }, 200);
    }
  });

  /* =============================================
     VARIANT SELECTOR
     ============================================= */
  document.addEventListener('click', function(e) {
    var pill = e.target.closest('.option-pill');
    if (!pill) return;
    if (pill.classList.contains('disabled')) return;

    var group = pill.closest('.product-option-pills');
    if (!group) return;

    group.querySelectorAll('.option-pill').forEach(function(p) {
      p.classList.remove('active');
    });
    pill.classList.add('active');

    var optionGroup = pill.closest('.product-option-group');
    if (optionGroup) {
      var labelSpan = optionGroup.querySelector('.product-option-label span');
      if (labelSpan) {
        labelSpan.textContent = pill.textContent.trim();
      }
    }

    var variantId = pill.getAttribute('data-variant-id');
    if (variantId) {
      updateSelectedVariant(variantId);
    }
  });

  function updateSelectedVariant(variantId) {
    var addBtn = document.querySelector('.product-add-btn');
    if (addBtn) {
      addBtn.setAttribute('data-variant-id', variantId);
    }
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /* =============================================
     QUANTITY CONTROL
     ============================================= */
  document.addEventListener('click', function(e) {
    var qtyBtn = e.target.closest('.product-quantity button');
    if (!qtyBtn) return;

    var qtyContainer = qtyBtn.closest('.product-quantity');
    var input = qtyContainer.querySelector('input');
    if (!input) return;

    var val = parseInt(input.value) || 1;
    if (qtyBtn.textContent.trim() === '+' || qtyBtn.textContent.trim() === '+') {
      val = Math.min(val + 1, 10);
    } else {
      val = Math.max(val - 1, 1);
    }
    input.value = val;
  });

  /* =============================================
     ACCORDION
     ============================================= */
  document.addEventListener('click', function(e) {
    var header = e.target.closest('.accordion-header');
    if (!header) return;

    var item = header.closest('.accordion-item');
    if (!item) return;

    var wasOpen = item.classList.contains('open');

    var accordion = item.closest('.accordion');
    if (accordion) {
      accordion.querySelectorAll('.accordion-item').forEach(function(i) {
        i.classList.remove('open');
      });
    }

    if (!wasOpen) {
      item.classList.add('open');
    }
  });

  /* =============================================
     ADD TO CART BUTTON — Main Product Page
     ============================================= */
  var addToCartBtn = document.querySelector('.product-add-btn');
  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', function(e) {
      e.preventDefault();
      var variantId = this.getAttribute('data-variant-id');
      var qtyInput = document.querySelector('.product-quantity input');
      var qty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

      if (!variantId) {
        var firstVariant = document.querySelector('[data-variant-id]');
        if (firstVariant) {
          variantId = firstVariant.getAttribute('data-variant-id');
        }
      }

      if (variantId) {
        addToCart(variantId, qty);
      }
    });
  }

  /* =============================================
     NEWSLETTER FORM
     ============================================= */
  document.querySelectorAll('.newsletter-form').forEach(function(form) {
    form.addEventListener('submit', function(e) {
      var btn = form.querySelector('button');
      var input = form.querySelector('input');
      if (btn && input && input.value) {
        setTimeout(function() {
          btn.textContent = 'Welcome!';
          input.value = '';
        }, 100);
      }
    });
  });

  /* =============================================
     FILTER PILLS (Collection page)
     ============================================= */
  document.addEventListener('click', function(e) {
    var pill = e.target.closest('.filter-pill');
    if (!pill) return;

    document.querySelectorAll('.filter-pill').forEach(function(p) {
      p.classList.remove('active');
    });
    pill.classList.add('active');
  });

  /* =============================================
     HERO SCROLL INDICATOR
     ============================================= */
  var scrollIndicator = document.querySelector('.hero-scroll-indicator');
  if (scrollIndicator) {
    scrollIndicator.addEventListener('click', function() {
      var target = document.getElementById('symbols') || document.querySelector('.section-padding');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }

  /* =============================================
     EXPOSE GLOBAL API
     ============================================= */
  window.Aurevra = {
    openCart: openCartDrawer,
    closeCart: closeCartDrawer,
    addToCart: addToCart,
    updateCartItem: updateCartItem,
    loadCart: loadCart,
    slideAnnouncement: slideAnnouncement
  };

})();
